import type { OrderStatus, PurchaseOrderStatus } from "@prisma/client";
import { orgSettingKeys } from "~/shared/constants/org.constants";
import {
  canGenerateOptInOrderDocumentForStatus,
  canGenerateOptInPurchaseOrderDocumentForStatus,
  hadCorruptOrderDocumentConfigRaw,
  isOrderDocumentSlugEnabled,
  isOrderEligibleForOrderDocumentSlug,
  type OrderDocumentOrderEligibilityContext,
  type OrderDocumentSlug,
  orderDocumentConfigKey,
  parseOrderDocumentsEnabledDiagnostics,
} from "~/shared/documents/order-document-settings";
import type { GetOrgSetting, OptInOrderDocumentDefinition } from "./types";

/**
 * Client-side opt-in order document gating (enablement, lifecycle, org requirements).
 * Fail-soft reads match server: corrupt non-empty `ORDER_DOCUMENTS_ENABLED` → disabled (server also logs warn).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 * @see docs/ORDER_LIFECYCLE_AND_DATA_CONSISTENCY.md
 * @see order-menu-document-extensions/registry.ts
 * @see shared/documents/order-document-settings.ts
 */

const enabledOrderDocumentSlugs = (getOrgSetting: GetOrgSetting): ReadonlySet<OrderDocumentSlug> =>
  new Set(parseOrderDocumentsEnabledDiagnostics(getOrgSetting(orgSettingKeys.orderDocumentsEnabled)).slugs);

/** True when hydrated org settings contain corrupt non-empty `ORDER_DOCUMENTS_ENABLED` JSON. */
export const hadCorruptOrderDocumentsEnabledSetting = (getOrgSetting: GetOrgSetting): boolean =>
  parseOrderDocumentsEnabledDiagnostics(getOrgSetting(orgSettingKeys.orderDocumentsEnabled)).hadCorruptRaw;

/** True when hydrated org settings contain corrupt non-empty per-slug config JSON. */
export const hadCorruptOrderDocumentConfigSetting = (slug: OrderDocumentSlug, getOrgSetting: GetOrgSetting): boolean =>
  hadCorruptOrderDocumentConfigRaw(slug, getOrgSetting(orderDocumentConfigKey(slug)));

/** Client-side opt-in document availability from `ORDER_DOCUMENTS_ENABLED`. */
export const isOptInOrderDocumentEnabled = (slug: OrderDocumentSlug, getOrgSetting: GetOrgSetting): boolean =>
  isOrderDocumentSlugEnabled(enabledOrderDocumentSlugs(getOrgSetting), slug);

/** Slug enabled + optional per-definition org base currency requirement. */
export const isOptInOrderDocumentClientAvailable = (
  definition: Pick<OptInOrderDocumentDefinition, "id" | "requiredOrgBaseCurrencyCode">,
  getOrgSetting: GetOrgSetting,
  orgBaseCurrencyCode: string,
): boolean => {
  if (definition.requiredOrgBaseCurrencyCode && orgBaseCurrencyCode !== definition.requiredOrgBaseCurrencyCode) {
    return false;
  }
  if (hadCorruptOrderDocumentConfigSetting(definition.id, getOrgSetting)) {
    return false;
  }
  return isOptInOrderDocumentEnabled(definition.id, getOrgSetting);
};

/** Settings nav: show when slug is enabled, including corrupt-config diagnostic routes. */
export const isOptInOrderDocumentSettingsNavVisible = (
  definition: Pick<OptInOrderDocumentDefinition, "id" | "requiredOrgBaseCurrencyCode" | "settingsNav">,
  getOrgSetting: GetOrgSetting,
  orgBaseCurrencyCode: string,
): boolean => {
  if (!definition.settingsNav) {
    return false;
  }
  if (definition.requiredOrgBaseCurrencyCode && orgBaseCurrencyCode !== definition.requiredOrgBaseCurrencyCode) {
    return false;
  }
  if (hadCorruptOrderDocumentsEnabledSetting(getOrgSetting)) {
    return true;
  }
  if (!isOptInOrderDocumentEnabled(definition.id, getOrgSetting)) {
    return false;
  }
  return (
    isOptInOrderDocumentClientAvailable(definition, getOrgSetting, orgBaseCurrencyCode) ||
    hadCorruptOrderDocumentConfigSetting(definition.id, getOrgSetting)
  );
};

type VisibleOptInOrderDocumentArgs = {
  definitions: readonly OptInOrderDocumentDefinition[];
  getOrgSetting: GetOrgSetting;
  orgBaseCurrencyCode: string;
  orderStatus: OrderStatus;
  hasUpdatePermission: boolean;
  orderEligibility: OrderDocumentOrderEligibilityContext;
};

/** Shared filter for Create Document menu extensions (registry-driven). */
export const filterVisibleOptInOrderDocuments = ({
  definitions,
  getOrgSetting,
  orgBaseCurrencyCode,
  orderStatus,
  hasUpdatePermission,
  orderEligibility,
}: VisibleOptInOrderDocumentArgs): OptInOrderDocumentDefinition[] =>
  definitions.filter(
    definition =>
      hasUpdatePermission &&
      isOptInOrderDocumentClientAvailable(definition, getOrgSetting, orgBaseCurrencyCode) &&
      canGenerateOptInOrderDocumentForStatus(orderStatus, definition.orderStatuses) &&
      isOrderEligibleForOrderDocumentSlug(definition.id, orderEligibility),
  );

/** Standalone POs share the registry; a linked PO generates these documents from its SO. */
export const filterVisibleOptInPurchaseOrderDocuments = ({
  definitions,
  getOrgSetting,
  orgBaseCurrencyCode,
  hasUpdatePermission,
  purchaseOrder,
}: Pick<VisibleOptInOrderDocumentArgs, "definitions" | "getOrgSetting" | "orgBaseCurrencyCode" | "hasUpdatePermission"> & {
  purchaseOrder: { status: PurchaseOrderStatus; orderId: string | null } | undefined;
}): OptInOrderDocumentDefinition[] => {
  if (
    !purchaseOrder ||
    purchaseOrder.orderId ||
    !hasUpdatePermission ||
    !canGenerateOptInPurchaseOrderDocumentForStatus(purchaseOrder.status)
  ) {
    return [];
  }
  return definitions.filter(definition => isOptInOrderDocumentClientAvailable(definition, getOrgSetting, orgBaseCurrencyCode));
};
